import { Analysis } from '../models/Analysis';
import { AnalysisStatus } from '../models/AnalysisStatus';
import { AnalysisType } from '../models/AnalysisType';
import { GDMSProcessData } from '../header-data/header-optional-data/header-gdms-popup/models/GDMSProcessData';
import { HeaderDataInfo } from '../models/HeaderData';
import { CriterionData } from '../models/criterions/CriterionData';
import { AnalysisPage } from '../models/AnalysisPage';
import { EditMode } from '../models/EditMode';

export class AnalysisUtils {
    public static readonly supportAnalysisTypes: Set<AnalysisType> = new Set([
        AnalysisType.SUPPORT,
        AnalysisType.SUPPORT_TMO,
        AnalysisType.SUPPORT_MUA,
        AnalysisType.FREE_SUPPORT
    ]);

    public static readonly groupAnalysisTypes: Set<AnalysisType> = new Set([
        AnalysisType.FREE_GROUP,
        AnalysisType.ADDITIONAL,
        AnalysisType.AVERAGE,
        AnalysisType.DERIVATIVE_GROUP,
        AnalysisType.MUA,
        AnalysisType.LEMO
    ]);

    public static readonly MM_RANGE_MIN = 1;
    public static readonly MM_RANGE_MAX = 13;
    public static readonly MM_RANGE_START_08 = 8;
    public static readonly MM_RANGE_START_13 = 13;

    public static isSupport(analysisType?: AnalysisType): boolean {
        return this.supportAnalysisTypes.has(AnalysisType[analysisType?.toString() as keyof typeof AnalysisType]);
    }

    public static isGroupAnalysis(analysisType?: AnalysisType): boolean {
        return this.groupAnalysisTypes.has(AnalysisType[analysisType?.toString() as keyof typeof AnalysisType]);
    }

    public static isGroupOrSupportOrTvg(analysisType?: AnalysisType) {
        return AnalysisUtils.isGroupAnalysis(analysisType) || AnalysisUtils.isSupport(analysisType) || AnalysisUtils.isTvgAnalysis(analysisType);
    }

    public static isFreeAnalysis(analysisType?: AnalysisType): boolean {
        return analysisType?.toString() === 'FREE';
    }

    public static isAvgAnalysis(analysisType: AnalysisType | undefined): boolean {
        return analysisType?.toString() === 'AVG';
    }

    public static isTvgAnalysis(analysisType: AnalysisType | undefined): boolean {
        return analysisType?.toString() === 'TVG';
    }

    public static isValidAnalysis(analysisStatus: AnalysisStatus | undefined): boolean {
        return analysisStatus?.toString() === 'VALID';
    }

    public static isMuaAnalysis(analysisType: AnalysisType | undefined): boolean {
        return analysisType?.toString() === 'MUA';
    }

    public static isLemoAnalysis(analysisType: AnalysisType | undefined): boolean {
        return analysisType?.toString() === 'LEMO';
    }

    public static isMuaSupportAnalysis(analysisType: AnalysisType | undefined): boolean {
        return analysisType?.toString() === 'SUPPORT_MUA';
    }

    public static isLemoSupportAnalysis(analysisType: AnalysisType | undefined): boolean {
        return analysisType?.toString() === 'SUPPORT_TMO';
    }

    public static isFreeSupportAnalysis(analysisType: AnalysisType | undefined): boolean {
        return analysisType?.toString() === 'FREE_SUPPORT';
    }

    public static isEditableTypeAnalysis(analysisType: AnalysisType | undefined): boolean {
        return !!analysisType && !this.isMuaAnalysis(analysisType);
    }

    public static isPipAnalysis(analysisType: AnalysisType | undefined): boolean {
        return AnalysisUtils.isGroupAnalysis(analysisType) || AnalysisUtils.isFreeAnalysis(analysisType);
    }

    public static minimumMandatoryHeaderDataFilledForSupport(headerData: HeaderDataInfo): boolean {
        return (
            !!headerData.workPlaceNumber &&
            headerData.workPlaceNumber.trim() !== '' &&
            !!headerData.description &&
            headerData.description.trim() !== '' &&
            !!headerData.plantCCDC &&
            headerData.plantCCDC.length > 0 &&
            !!headerData.technologyCode &&
            headerData.technologyCode.trim() !== ''
        );
    }

    public static numberIdentWkplcAndTaskLengthFilled(headerData: HeaderDataInfo): boolean {
        return this.numberIdenticalWorkPlaceFilled(headerData) && this.taskLengthFilled(headerData);
    }

    public static numberIdenticalWorkPlaceFilled(headerData: HeaderDataInfo): boolean {
        return !!headerData.numberIdenticalWorkPlace;
    }

    public static taskLengthFilled(headerData: HeaderDataInfo): boolean {
        return headerData.taskLength != null;
    }

    public static mandatoryHeaderDataFilledForNonSupport(headerData: HeaderDataInfo | undefined) {
        if (!headerData) {
            return false;
        }
        return AnalysisUtils.minimumMandatoryHeaderDataFilledForSupport(headerData) && AnalysisUtils.numberIdentWkplcAndTaskLengthFilled(headerData);
    }

    public static mandatoryHeaderDataFilledForAvg(headerData: HeaderDataInfo) {
        return AnalysisUtils.minimumMandatoryHeaderDataFilledForSupport(headerData) && AnalysisUtils.numberIdenticalWorkPlaceFilled(headerData);
    }

    public static hasValidFromDate(headerData: HeaderDataInfo): boolean {
        return headerData.validFrom != undefined;
    }

    public static hasMissingMandatoryCriteria(headerData: HeaderDataInfo | undefined): boolean {
        if (!headerData) {
            return true;
        }
        if (headerData.analysisForm?.toString().includes('FULL')) {
            let mandatoryMMs = headerData.selectedMasterDataTechnology?.mandatoryCriteria?.split(',') ?? [];
            return mandatoryMMs.some(mandatory => !this.criterionFilled(headerData.criterionsData[Number(mandatory) - 1]));
        } else {
            let mandatoryMMs = headerData.selectedMasterDataTechnology?.mandatoryCriteriaIto?.split(',') ?? [];
            return mandatoryMMs.some(mandatory => !this.criterionFilled(headerData.criterionsData[Number(mandatory) - 1]));
        }
    }

    public static criterionFilled(criterion: CriterionData): boolean {
        return criterion.trafficLightColor != null || criterion.shiValue! > 0 || (criterion.noStress ?? false);
    }

    public static analysisForClipboardWithGdmsData(analysis: Analysis): Analysis {
        return {
            ...analysis,
            gdmsProcessData: {
                ...analysis.gdmsProcessData,
                status: 'draft',
                did: null,
                objectId: null,
                analysisId: null
            } as GDMSProcessData
        };
    }

    public static buildNewAnalysisFromClipboard(analysis: Analysis): Analysis {
        return {
            ...analysis,
            id: null,
            analysisKey: null,
            connectedSingleAnalysis: [],
            groupList: [],
            editMode: EditMode.EDIT,
            maxVersion: 1,
            version: 1,
            validFrom: null,
            validTo: null,
            status: AnalysisStatus.DRAFT,
            isGDMSAnalysis: false,
            gdmsProcessData: null
        } as unknown as Analysis;
    }

    public static generateSourceId(): number {
        return Date.now() + Math.floor(Math.random() * 1000000);
    }

    public static extractCRorMMNumber = (literalString: string, prefix: string): number | null => {
        const regex = new RegExp(`^${prefix}(\\d{2})$`);
        const match = literalString.match(regex);
        return match ? parseInt(match[1], 10) : null;
    };

    public static getEnabledMMRange(firstCriterionToShow: AnalysisPage): { enabledFrom: number; enabledTo: number } {
        let enabledFrom = this.MM_RANGE_MIN;
        if (firstCriterionToShow === AnalysisPage.MM13) {
            enabledFrom = this.MM_RANGE_START_13;
        } else if (firstCriterionToShow === AnalysisPage.MM08) {
            enabledFrom = this.MM_RANGE_START_08;
        }
        return { enabledFrom, enabledTo: this.MM_RANGE_MAX };
    }
}
